/**
 * Quality Co-Pilot turn model + the SSE-event reducer.
 *
 * Kept in a sibling `.ts` (not the page `.tsx`) so it is pure + unit-testable and
 * so the page module exports only its component (ESLint react-refresh rule).
 *
 * The Co-Pilot renders the SAME block-by-block trace as the Agent Console — its
 * thinking, its tool calls (name · status · input · result), then its answer —
 * by folding each streamed `ConsoleEvent` into an `AssistantBlock[]` with the
 * shared helpers in services/agentConsole. The voice-specific rule: a
 * `voice_reply` tool-use becomes the spoken rundown (`spoken`) and is kept OUT of
 * the block list, so it never renders as a tool row. Resuming a saved thread goes
 * through the SHARED walk (`detailToTurns`); this file only layers the Co-Pilot's
 * admin usage line on top.
 */
import { detailToTurns } from '../components/agentConsole/detailToTurns';
import {
  addUsageToTotals,
  appendLiveText,
  appendLiveThinking,
  applyLiveToolResult,
  EMPTY_TOTALS,
  toolCallViewFromUse,
  type AssistantBlock,
  type ConsoleEvent,
  type ConversationDetail,
  type ConversationTotals,
  type MessageRead,
  type UsageEvent,
} from '../services/agentConsole';
import { isVoiceReplyTool, spokenSummaryFromToolUse } from '../services/qualityCopilot';
import {
  liftAgentFileCards,
  mergeAgentFiles,
} from '../components/agentConsole/agentFileCards';
import type { AgentFileData } from '../components/agentConsole/agentFileCards';
import {
  draftCardFromResultSummary,
  isOrderReadbackTool,
  liftOrderCards,
  readbackFromToolUse,
} from './qualityCopilotOrderCards';
import type { DraftCardData, OrderReadbackData } from './qualityCopilotOrderCards';

export interface CopilotTurn {
  id: string;
  role: 'user' | 'assistant';
  // user side
  text?: string;
  viaVoice?: boolean;
  attachments?: string[]; // filenames of photos sent with this user turn
  // True when the pipeline (not a person) typed this user message — the
  // investigator's seeded kickoff. The triage board hides these (Scott,
  // review bs63ro): Merlin's filing already opens the pane, so the seed is
  // noise there. Kept in the model so the audit trail stays complete.
  systemSeeded?: boolean;
  // assistant side
  spoken?: string | null; // voice_reply summary → playable rundown bubble
  // Merlin order cards, lifted from the order tools' frames (see
  // qualityCopilotOrderCards.ts). Latest-wins within a turn.
  readback?: OrderReadbackData | null; // propose_order_readback → read-back card
  draftCard?: DraftCardData | null; // create_draft_order success → draft card
  // attach_file successes → downloadable file cards (charts, data extracts).
  // A turn may attach several files, so this ACCUMULATES in stream order.
  agentFiles?: AgentFileData[];
  blocks?: AssistantBlock[]; // the thinking / tool / answer trace (answer leads with "At a glance")
  usage?: UsageEvent | null; // end-of-turn token/cost line
  streaming?: boolean;
  error?: string | null;
  // True when the user hit Stop mid-turn: the partial reply above stays in the
  // thread with a calm "stopped" note (NOT an error), and the composer is ready
  // for the next message in the same conversation.
  cancelled?: boolean;
  // A server-side recovery note streamed ahead of the answer (today: the
  // conversation's history no longer fit the model, so this reply started from
  // a fresh context). Rendered as a calm note above the answer, never an error.
  notice?: string | null;
  // Real audit ids for 👍/👎 feedback: the persisted assistant message id (from
  // the `done` frame live, or the saved row on resume) + the model that answered.
  messageId?: string | null;
  model?: string | null;
  // When the message happened — persisted created_at on resume, client send
  // time live. Shown on the bubble (browser-local, portal convention).
  createdAt?: string | null;
}

/**
 * Fold one streamed `ConsoleEvent` into an assistant turn, returning ONLY the
 * changed fields (pure — no TTS side effects; the page captures the latest
 * `voice_reply` summary off the same event and plays it once on `done`).
 */
export function reduceCopilotEvent(
  turn: CopilotTurn,
  evt: ConsoleEvent,
): Partial<CopilotTurn> {
  const blocks = turn.blocks ?? [];
  switch (evt.type) {
    case 'text':
      return { blocks: appendLiveText(blocks, evt.text) };
    case 'thinking':
      return { blocks: appendLiveThinking(blocks, evt.text) };
    case 'tool_use':
      // The spoken rundown is its own layer, never a tool row in the trace. Keep
      // the latest non-empty value. (The at-a-glance lives in the written answer.)
      if (isVoiceReplyTool(evt.name)) {
        const spoken = spokenSummaryFromToolUse(evt);
        return { spoken: spoken || turn.spoken };
      }
      // The read-back card is its own layer too — the card replaces the chip.
      if (isOrderReadbackTool(evt.name)) {
        const readback = readbackFromToolUse(evt);
        return { readback: readback ?? turn.readback };
      }
      return {
        blocks: [
          ...blocks,
          { kind: 'tool', tool: toolCallViewFromUse(evt, `live-${blocks.length}`) },
        ],
      };
    case 'tool_result': {
      // A successful create_draft_order announces itself in the result text —
      // recognize it there (no tool_use_id bookkeeping) and lift the draft card.
      const draftCard = evt.is_error ? null : draftCardFromResultSummary(evt.summary);
      const applied = applyLiveToolResult(
        blocks,
        evt.tool_use_id,
        evt.is_error,
        evt.summary,
      );
      // A successful attach_file frame becomes a file card the moment its ack
      // lands — lifted out of the trace (pending/errored chips stay).
      const { blocks: kept, files } = liftAgentFileCards(applied);
      return {
        blocks: kept,
        ...(draftCard ? { draftCard } : {}),
        ...(files.length
          ? { agentFiles: mergeAgentFiles(turn.agentFiles, files) }
          : {}),
      };
    }
    case 'result':
      return { usage: evt };
    case 'cancelled':
      // A user Stop — a calm terminal state, never an error toast. Whatever
      // streamed so far stays in the turn; the `done` frame follows right after.
      return { cancelled: true, streaming: false };
    case 'notice':
      return { notice: evt.message };
    case 'error':
      return { error: evt.message, streaming: false };
    case 'done':
      // Capture the real audit message id + the model that answered so the
      // settled turn can carry 👍/👎 feedback (the portal turn is ephemeral).
      return {
        streaming: false,
        messageId: evt.message_id ?? null,
        model: evt.model ?? null,
      };
    default:
      return {};
  }
}

/**
 * Running token/cost total across a conversation — the sum of every assistant
 * turn's end-of-turn `usage` (user turns + turns still streaming carry none).
 * Mirrors the Agent Console's server-side `ConversationTotals` using the same
 * `addUsageToTotals` fold, so the bottom-right "Σ … tokens · $…" meter matches
 * the per-turn lines. Admin-only at the call site; the math itself is pure.
 */
export function conversationTotals(turns: CopilotTurn[]): ConversationTotals {
  return turns.reduce(
    (acc, t) => (t.role === 'assistant' && t.usage ? addUsageToTotals(acc, t.usage) : acc),
    EMPTY_TOTALS,
  );
}

/**
 * Resume-path lift of the Merlin ORDER cards from a saved turn's blocks
 * (read-back + draft), each dropping its own chip. Agent-file cards are lifted
 * centrally by the shared `detailToTurns` walk, so they arrive on the rebuilt
 * turn already extracted.
 */
function liftOrderCardsForResume(blocks: AssistantBlock[]) {
  const orders = liftOrderCards(blocks);
  return {
    blocks: orders.blocks,
    readback: orders.readback,
    draftCard: orders.draftCard,
  };
}

/**
 * Rebuild a SAVED conversation into the Co-Pilot's turn model so a resumed thread
 * renders identically to one just streamed. The shared `detailToTurns` does the
 * walk (block reconstruction + the voice rule); the Co-Pilot layers its admin
 * usage line on each assistant turn from the persisted token/cost.
 */
export function detailToCopilotTurns(detail: ConversationDetail): CopilotTurn[] {
  const turns = detailToTurns(detail).map<CopilotTurn>((t) =>
    t.role === 'user'
      ? {
          id: t.id,
          role: 'user',
          text: t.text,
          createdAt: t.createdAt ?? null,
          // The shared walk marks pipeline-typed kickoffs (raw_blocks marker);
          // surfaces that want to hide or restyle them read this flag.
          ...(t.kind ? { systemSeeded: true } : {}),
          // Files claimed onto this user message (post-turn) → the 📎 note, so a
          // resumed thread shows what rode in, same as it did live.
          attachments: t.attachments?.length
            ? t.attachments.map((a) => a.filename)
            : undefined,
        }
      : (({ blocks, readback, draftCard }) => ({
          id: t.id,
          role: 'assistant' as const,
          spoken: t.spoken ?? null,
          readback,
          draftCard,
          // Agent-file cards come pre-lifted from the shared detail walk.
          agentFiles: t.agentFiles,
          // A persisted recovery notice (fresh context) rides onto this turn.
          notice: t.notice ?? null,
          blocks,
          usage: t.message ? usageFromMessage(t.message) : null,
          streaming: false,
          error: null,
          // Resume carries the real persisted ids so feedback works on reload too.
          messageId: t.message?.id ?? null,
          model: t.message?.model ?? null,
          createdAt: t.createdAt ?? null,
        }))(liftOrderCardsForResume(t.blocks ?? [])),
  );
  // A thread reloaded in the `cancelled` state ended with a user Stop: label the
  // last assistant turn so its (possibly partial) reply reads as interrupted on
  // resume, same as it did live. The state is conversation-level and resets to
  // `running` on the next turn, so only the trailing turn can be the stopped one.
  if (detail.conversation.state === 'cancelled') {
    for (let i = turns.length - 1; i >= 0; i -= 1) {
      if (turns[i].role === 'assistant') {
        turns[i] = { ...turns[i], cancelled: true };
        break;
      }
    }
  }
  return turns;
}

/**
 * Fold a saved assistant message's persisted token/cost back into a UsageEvent so
 * the per-turn line and the running total render on resume. Null when the row has
 * no usage at all (e.g. a turn that failed before any billing was recorded).
 */
function usageFromMessage(message: MessageRead): UsageEvent | null {
  if (
    message.input_tokens == null &&
    message.output_tokens == null &&
    message.cost_usd == null
  ) {
    return null;
  }
  return {
    type: 'result',
    stop_reason: message.stop_reason,
    input_tokens: message.input_tokens,
    output_tokens: message.output_tokens,
    cache_creation_input_tokens: message.cache_creation_input_tokens,
    cache_creation_1h_input_tokens: message.cache_creation_1h_input_tokens ?? null,
    cache_read_input_tokens: message.cache_read_input_tokens,
    cost_usd: message.cost_usd,
  };
}
